import React, { useEffect, useState } from 'react';
import { DeleteOutlined, DownloadOutlined, LinkOutlined, PictureOutlined } from '@ant-design/icons';
import { Alert, Button, ConfigProvider, Modal, Radio, Tabs, Tooltip, type TabsProps } from 'antd';
import type { BrandAsset, BrandResources } from 'common/brand-kit/types';
import { getGeneralGuidelines, setGeneralGuidelines, brandResourcesError } from 'common/brand-kit/resources';
import { setPrimaryBrandLink } from 'common/brand-kit/projection';
import {
	createDesignModeValue,
	createDesignTokenId,
	getDefaultDesignModeSelection,
	resolveDesignModeValue,
	type DesignModeGroup,
} from 'common/design-tokens';
import DesignSystemEditor from 'admin/components/common/DesignSystemEditor';
import DesignPresetsEditor from 'admin/components/common/DesignSystemEditor/DesignPresetsEditor';
import type { StoryMediaQuery } from 'types/story';
import DesignModeControl from 'admin/components/common/DesignModeControl';
import DesignModeValueField from 'admin/components/common/DesignModeValueField';
import { Select as FormSelect, TextField } from 'admin/components/common/Form';
import { TextAreaPure } from 'admin/components/common/Form/TextArea';
import Text from 'admin/components/common/Text';
import ResourceAddButton from 'admin/components/common/ResourceEditor/ResourceAddButton';
import ResourceEmptyState from 'admin/components/common/ResourceEditor/ResourceEmptyState';
import { antdThemeDark, antdThemeLight } from 'admin/components/common/Theme';
import MediaField from 'admin/components/common/Form/MediaField';
import t from 'utils/translate';
import { generateId } from 'utils/generate-id';
import AssetField from './AssetField';
import css from './BrandResourcesEditor.scss';

export type AssetUpload = (asset: File) => Promise<Error | { asset: string }>;
export function brandResourcesFormError(value?: BrandResources) {
	return (
		brandResourcesError(value) ??
		(value?.design.assets.assets.some(asset =>
			[asset.value.base, ...(asset.value.overrides ?? []).map(item => item.value)].some(url =>
				Object.values(MediaField.errors).includes(url as never)
			)
		)
			? 'Resolve failed image uploads before saving.'
			: undefined)
	);
}
type Props = {
	value: BrandResources;
	mediaQuery?: StoryMediaQuery;
	onChange: (value: BrandResources) => void;
	fonts: { fontFamily: string }[];
	fontContext: 'organization' | 'story';
	theme?: 'light' | 'dark';
	disabled?: boolean;
	identity?: React.ReactNode;
	onUpload: AssetUpload;
	onBusyChange: (busy: boolean) => void;
	getAssetUsageCount?: (id: string) => number;
	getPresetUsageCount?: (id: string) => number;
};
const ASSET_ROLES = [
	'logo',
	'compactLogo',
	'share',
	'favicon',
	'other',
] as const satisfies readonly BrandAsset['role'][];
function getInitialAssetModeId(modeGroups?: DesignModeGroup[]) {
	const group = modeGroups?.find(item => item.id === 'theme') ?? modeGroups?.[0];
	return group?.defaultModeId;
}

function TabLabel({ children, count }: { children: React.ReactNode; count?: number }) {
	return (
		<span className={css.tabLabel}>
			{children}
			{typeof count === 'number' ? <span>{count}</span> : null}
		</span>
	);
}

function ResourcesTab({
	description,
	actions,
	children,
}: {
	description: React.ReactNode;
	actions?: React.ReactNode;
	children: React.ReactNode;
}) {
	return (
		<div className={css.tabPane}>
			<div className={css.paneHeader}>
				<Text size={Text.size.paragraph} weight={Text.weight.normal} className={css.paneDescription} compact>
					{description}
				</Text>
				{actions}
			</div>
			{children}
		</div>
	);
}

export default function BrandResourcesEditor({
	value: kit,
	mediaQuery,
	onChange,
	fonts,
	fontContext,
	theme = 'light',
	disabled = false,
	identity,
	onUpload,
	onBusyChange,
	getAssetUsageCount,
	getPresetUsageCount,
}: Props) {
	const [modal, contextHolder] = Modal.useModal();
	const error = brandResourcesFormError(kit);
	const [activeTab, setActiveTab] = useState(identity ? 'identity' : 'tokens');
	const assetThemeGroup = kit.design.modeGroups.find(group => group.id === 'theme');
	const [requestedAssetModeId, setRequestedAssetModeId] = useState(() =>
		getInitialAssetModeId(kit.design.modeGroups)
	);
	const activeAssetModeId = assetThemeGroup?.modes.some(mode => mode.id === requestedAssetModeId)
		? requestedAssetModeId
		: assetThemeGroup?.defaultModeId;
	const assetModeSelection = {
		...getDefaultDesignModeSelection(kit.design.modeGroups),
		...(assetThemeGroup && activeAssetModeId ? { [assetThemeGroup.id]: activeAssetModeId } : {}),
	};
	useEffect(() => setRequestedAssetModeId(getInitialAssetModeId(kit.design.modeGroups)), [kit.design.modeGroups]);
	const updateKit = (next: BrandResources | ((current: BrandResources) => BrandResources)) =>
		onChange(typeof next === 'function' ? next(kit) : next);
	const addLink = () =>
		updateKit(current => ({
			...current,
			links: [
				...current.links,
				{
					id: createDesignTokenId(
						'link',
						current.links.map(item => item.id)
					),
					label: '',
					url: '',
				},
			],
		}));
	const addAsset = () =>
		updateKit(current => ({
			...current,
			design: {
				...current.design,
				assets: {
					...current.design.assets,
					assets: [
						...current.design.assets.assets,
						{
							// Deleted asset references must never bind to an unrelated newly created asset.
							id: createDesignTokenId(
								`asset-${generateId(12)}`,
								current.design.assets.assets.map(item => item.id)
							),
							name: 'New asset',
							role: 'logo',
							type: 'image',
							value: createDesignModeValue(''),
						},
					],
				},
			},
		}));
	const updateAsset = (assetId: string, update: (asset: BrandAsset) => BrandAsset) =>
		updateKit(current => ({
			...current,
			design: {
				...current.design,
				assets: {
					...current.design.assets,
					assets: current.design.assets.assets.map(asset => (asset.id === assetId ? update(asset) : asset)),
				},
			},
		}));

	const tabItems: TabsProps['items'] = [
		...(identity
			? [
					{
						key: 'identity',
						label: <TabLabel>Identity</TabLabel>,
						children: (
							<ResourcesTab description="Name and default Story language.">{identity}</ResourcesTab>
						),
					},
				]
			: []),
		{
			key: 'links',
			label: <TabLabel count={kit.links.length}>{t('brandKit.links')}</TabLabel>,
			children: (
				<ResourcesTab
					description="The primary link is used as the default logo destination."
					actions={
						kit.links.length > 0 ? (
							<ResourceAddButton theme={theme} disabled={disabled} onClick={addLink}>
								{t('brandKit.addLink')}
							</ResourceAddButton>
						) : undefined
					}
				>
					<div className={css.linkList}>
						{kit.links.map(link => (
							<div className={css.link} key={link.id}>
								<TextField
									theme={theme}
									aria-label="Link label"
									placeholder="Label"
									value={link.label}
									onChange={event =>
										updateKit({
											...kit,
											links: kit.links.map(item =>
												item.id === link.id ? { ...item, label: event.target.value } : item
											),
										})
									}
								/>
								<TextField
									theme={theme}
									aria-label="Link URL"
									placeholder="https://"
									value={link.url}
									onChange={event =>
										updateKit({
											...kit,
											links: kit.links.map(item =>
												item.id === link.id ? { ...item, url: event.target.value } : item
											),
										})
									}
								/>
								<Radio
									checked={!!link.isPrimary}
									onClick={() =>
										updateKit({
											...kit,
											links: setPrimaryBrandLink(kit.links, link.isPrimary ? null : link.id),
										})
									}
								>
									{t('brandKit.primary')}
								</Radio>
								<Tooltip title={t('designTokens.delete')}>
									<Button
										className={css.rowDelete}
										aria-label={t('designTokens.delete')}
										danger
										type="text"
										icon={<DeleteOutlined />}
										onClick={() =>
											updateKit({
												...kit,
												links: kit.links.filter(item => item.id !== link.id),
											})
										}
									/>
								</Tooltip>
							</div>
						))}
						{!kit.links.length ? (
							<ResourceEmptyState
								title="No links yet"
								description="Add website or social links. Choose a primary link for the default logo destination."
								icon={<LinkOutlined />}
								action={
									<ResourceAddButton prominent theme={theme} disabled={disabled} onClick={addLink}>
										{t('brandKit.addLink')}
									</ResourceAddButton>
								}
							/>
						) : null}
					</div>
				</ResourcesTab>
			),
		},
		{
			key: 'assets',
			label: <TabLabel count={kit.design.assets.assets.length}>{t('brandKit.assets')}</TabLabel>,
			children: (
				<ResourcesTab
					description="Logos, images and icons to use across your elements."
					actions={
						kit.design.assets.assets.length > 0 ? (
							<ResourceAddButton theme={theme} disabled={disabled} onClick={addAsset}>
								{t('brandKit.addAsset')}
							</ResourceAddButton>
						) : undefined
					}
				>
					{assetThemeGroup && kit.design.assets.assets.length > 0 ? (
						<DesignModeControl
							value={activeAssetModeId}
							modes={assetThemeGroup.modes}
							onChange={setRequestedAssetModeId}
							disabled={disabled}
						/>
					) : null}
					<div className={css.assets}>
						{kit.design.assets.assets.map(asset => {
							const assetUrl = resolveDesignModeValue(asset.value, assetModeSelection);
							const downloadable = /^https?:\/\//.test(assetUrl);
							const deleteAsset = () =>
								updateKit(current => ({
									...current,
									design: {
										...current.design,
										assets: {
											...current.design.assets,
											assets: current.design.assets.assets.filter(item => item.id !== asset.id),
										},
									},
								}));

							return (
								<div className={css.asset} key={asset.id}>
									<div className={css.assetPreview}>
										<DesignModeValueField
											value={asset.value}
											selection={assetModeSelection}
											groups={kit.design.modeGroups}
											disabled={disabled}
											onChange={next => updateAsset(asset.id, item => ({ ...item, value: next }))}
										>
											{({ value: activeAssetUrl, setValue }) => (
												<AssetField
													id={asset.id}
													value={activeAssetUrl}
													onUpload={onUpload}
													theme={theme}
													onBusyChange={onBusyChange}
													key={JSON.stringify(assetModeSelection)}
													onChange={setValue}
												/>
											)}
										</DesignModeValueField>
									</div>
									<div className={css.assetFields}>
										<TextField
											theme={theme}
											aria-label="Asset name"
											placeholder="Asset name"
											value={asset.name}
											onChange={event =>
												updateAsset(asset.id, item => ({
													...item,
													name: event.target.value,
												}))
											}
										/>
										<div className={css.assetToolbar}>
											<FormSelect
												theme={theme}
												disabled={disabled}
												className={css.assetRole}
												label={{ children: 'Type', htmlFor: `asset-role-${asset.id}` }}
												value={asset.role}
												options={ASSET_ROLES.map(role => ({
													value: role,
													label: t(`brandKit.roles.${role}`),
												}))}
												eventListeners={{
													onChange: role =>
														updateAsset(asset.id, item => ({ ...item, role })),
												}}
											/>
											<div className={css.assetActions}>
												{downloadable ? (
													<Button
														type="text"
														icon={<DownloadOutlined />}
														href={assetUrl.split('?')[0]}
														target="_blank"
														rel="noreferrer noopener"
													>
														Download original
													</Button>
												) : null}
												<Tooltip title={t('designTokens.delete')}>
													<Button
														className={css.rowDelete}
														aria-label={t('designTokens.delete')}
														danger
														type="text"
														icon={<DeleteOutlined />}
														onClick={() => {
															const count = getAssetUsageCount?.(asset.id) ?? 0;
															if (!count) deleteAsset();
															else
																modal.confirm({
																	title: `Delete ${asset.name}?`,
																	content: `${count} building block(s) use this asset. Their references will remain unresolved until reassigned.`,
																	okText: 'Delete asset',
																	okButtonProps: { danger: true },
																	onOk: deleteAsset,
																});
														}}
													/>
												</Tooltip>
											</div>
										</div>
										<TextAreaPure
											theme={theme}
											label={{ children: 'Description' }}
											autoSize={{ minRows: 2, maxRows: 5 }}
											aria-label="Asset description"
											placeholder="Optional context for the AI assistant"
											value={asset.description ?? ''}
											onChange={event =>
												updateAsset(asset.id, item => ({
													...item,
													description: event.target.value,
												}))
											}
										/>
									</div>
								</div>
							);
						})}
						{!kit.design.assets.assets.length ? (
							<ResourceEmptyState
								title="No assets yet"
								description="Add a logo, image or icon to reuse across your elements."
								icon={<PictureOutlined />}
								action={
									<ResourceAddButton prominent theme={theme} disabled={disabled} onClick={addAsset}>
										{t('brandKit.addAsset')}
									</ResourceAddButton>
								}
							/>
						) : null}
					</div>
				</ResourcesTab>
			),
		},
		{
			key: 'guidelines',
			label: <TabLabel>{t('brandKit.guidelines')}</TabLabel>,
			children: (
				<ResourcesTab description="Rules and context for the AI assistant.">
					<TextAreaPure
						theme={theme}
						aria-label="Guidelines"
						placeholder="Add tone of voice, writing guidelines or rules for the AI assistant."
						autoSize={{ minRows: 5, maxRows: 12 }}
						value={getGeneralGuidelines(kit.guidelines)}
						onChange={event =>
							updateKit({ ...kit, guidelines: setGeneralGuidelines(kit.guidelines, event.target.value) })
						}
					/>
				</ResourcesTab>
			),
		},
		{
			key: 'tokens',
			label: (
				<TabLabel
					count={kit.design.tokens.collections.reduce(
						(count, collection) => count + collection.tokens.length,
						0
					)}
				>
					{t('designTokens.title')}
				</TabLabel>
			),
			children: (
				<ResourcesTab
					description={
						fontContext === 'organization'
							? 'Reusable values copied into new Stories.'
							: 'Reusable values shared by every card in this Story.'
					}
				>
					<DesignSystemEditor
						value={kit.design}
						onChange={design => updateKit({ ...kit, design })}
						fonts={fonts}
						fontContext={fontContext}
						theme={theme}
						disabled={disabled}
					/>
				</ResourcesTab>
			),
		},
		{
			key: 'presets',
			label: <TabLabel count={kit.design.presets?.length ?? 0}>{t('presets.title')}</TabLabel>,
			children: (
				<ResourcesTab description={t('presets.help')}>
					<DesignPresetsEditor
						value={kit.design}
						onChange={design => updateKit({ ...kit, design })}
						fonts={fonts}
						mediaQuery={mediaQuery}
						getPresetUsageCount={getPresetUsageCount}
						theme={theme}
						disabled={disabled}
					/>
				</ResourcesTab>
			),
		},
	];

	return (
		<ConfigProvider theme={theme === 'dark' ? antdThemeDark : antdThemeLight} componentDisabled={disabled}>
			{contextHolder}
			<fieldset disabled={disabled} className={css.root} data-theme={theme}>
				<Tabs
					className={css.tabs}
					activeKey={activeTab}
					animated={false}
					items={tabItems}
					onChange={setActiveTab}
				/>
				{error ? <Alert type="error" title={error} /> : null}
			</fieldset>
		</ConfigProvider>
	);
}
